import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  resetFilterData,
  setFilterDataField,
  setGridCurrentPage,
  setPreventiveAppointments,
} from "src/redux/slices/preventiveAppointmentSlice";
import {
  getAppointmentTypes,
  getOnHoldReasons,
} from "src/redux/slices/lookupSlice";
import { useEffect, useRef, useState, useCallback } from "react";
import { getFieldName, convertIsoStringToDate } from "src/utils/helpers";
import moment from "moment";
import { toast } from "react-toastify";
import useOnPageLeave from "src/hooks/useOnPageLeave";
import ToggleFilter from "src/components/ToggleFilter/ToggleFilter";
import useResize from "src/hooks/useResize";
import FilterActionButtons from "src/components/FilterActionButtons/FilterActionButtons";
import { isCivilDefenseUser } from "src/utils/helpers";
import {
  isSelectedFilterStatusIsCompleted,
  onHideCalendar,
  onShowCalendar,
} from "src/utils/common";
import { showHistoricalDataChangeSource, Statuses } from "src/resources/enum";
import { setChangeHistoricalDataSource } from "src/redux/slices/sharedSlice";
import {
  getServiceTerritories,
  setServiceTerritories,
} from "src/redux/slices/lookupSlice";
import { MultiSelect } from "primereact/multiselect";

const PreventiveAppointmentFilter = () => {
  const dispatch = useDispatch();
  const startDateRef = useRef<Calendar | null>(null);
  const pathName = window.location.pathname;
  const [isBreached, setIsBreached] = useState<any>(
    pathName?.includes("breached") ? true : false
  );
  const endDateRef = useRef<Calendar | null>(null);
  const { t } = useTranslation(["common", "assignBuilding"]);
  const bmcList = useSelector((state: any) => state.civilDefense.bmcList);
  const citiesOptions = useSelector((state: any) => state.lookup.citiesOptions);
  const { isMobile } = useResize();
  const [isFilterShown, setIsFilterShown] = useState(!isMobile);

  const {
    propertyId,
    status,
    territoryId,
    company,
    startAppointmentDate,
    endAppointmentDate,
    slaBreached,
    contractNumber,
    cityId,
    PRPNumber,
    companyTradeLicenseNumber,
    propertyTradeLicense,
  } = useSelector((state: any) => state.preventiveAppointments?.filterData);

  const { language, appointmentsStatuses } = useSelector(
    (state: any) => state.lookup
  );

  const [viewDate, setViewDate] = useState<Date | null>(null);

  const sLAOptions = [
    { id: t("exceeded"), value: true },
    { id: t("onTrack"), value: false },
  ];
  const assignedBuildings = useSelector(
    (state: any) => state.lookup.assignedBuildings
  );
  const { serviceTerritories, allServiceTerritories } = useSelector(
    (state: any) => state.lookup
  );

  useEffect(() => {
    // reset filter data and search text
    dispatch(getAppointmentTypes() as any);
    dispatch(getOnHoldReasons() as any);
  }, [dispatch]);

  const handleFormSubmit = (e: any) => {
    e.preventDefault();
  };

  const handleFilter = () => {
    goToGridFirstPage();
  };

  const goToGridFirstPage = useCallback(() => {
    dispatch(setGridCurrentPage(0));
  }, []);

  const handleFormFieldChange = (e: any) => {
    let name, value;
    if (e.target) {
      name = e.target.name;
      value = e.target.value;
    } else {
      name = e.name;
      value = e.value;
    }
    //check 3la el dates
    // if (name.includes('Date')) {
    //   value = new Date(value);
    // }
    if (name === "startAppointmentDate" && endAppointmentDate) {
      const startDate = moment(value);
      const endDate = moment(endAppointmentDate);
      if (startDate.isAfter(endDate)) {
        toast.error(t("startEndDateValidation"));
        dispatch(setFilterDataField({ field: "endAppointmentDate", value }));
      }
    }
    dispatch(setFilterDataField({ field: name, value }));
    if (name === "status") {
      dispatch(
        setChangeHistoricalDataSource(
          showHistoricalDataChangeSource.Appointment
        )
      );
      dispatch(
        setFilterDataField({
          field: "showHistoricalData",
          value: isSelectedFilterStatusIsCompleted(appointmentsStatuses, value),
        })
      );
    }
  };

  const handleClearFilterForm = (): void => {
    dispatch(resetFilterData());
    goToGridFirstPage();
  };

  const handleCityChange = (e: any) => {
    handleFormFieldChange(e);
    dispatch(setFilterDataField({ field: "territoryId", value: "" }));
    dispatch(setServiceTerritories([]));
    dispatch(getServiceTerritories(false, e.value) as any);
  };

  const onPageLeave = useCallback(() => {
    dispatch(resetFilterData());
    dispatch(setPreventiveAppointments([]));
  }, []);
  useOnPageLeave(onPageLeave);

  const toggleFilter = () => {
    setIsFilterShown(!isFilterShown);
  };
  return (
    <>
      <ToggleFilter onClick={toggleFilter} />
      <div
        className={`pt-3 pb-2 px-2 maintenance-requests-filter assigned-buildings-filter ${
          !isMobile || isFilterShown ? "d-flex" : "d-none"
        } ${isMobile ? "my-3" : ""} `}
      >
        <p className="d-flex m-0 me-2 ms-2 mb-2 fit-content filter-by-label">
          {t("filterBy")}
        </p>
        <form onSubmit={handleFormSubmit}>
          <div className="filter-row mb-2">
            <Dropdown
              filter
              resetFilterOnHide
              emptyFilterMessage={t("noResultsFound")}
              id="statusId"
              options={appointmentsStatuses}
              placeholder={t("status")}
              className="status-dropdown"
              optionLabel={getFieldName(language)}
              optionValue="id"
              panelClassName="status-dropdown-panel"
              onChange={handleFormFieldChange}
              value={status}
              name="status"
            />
            <Dropdown
              filter
              resetFilterOnHide
              emptyFilterMessage={t("noResultsFound")}
              id="propertyId"
              options={assignedBuildings}
              placeholder={t("filterByBuilding")}
              className="status-dropdown"
              panelClassName="status-dropdown-panel"
              onChange={handleFormFieldChange}
              value={propertyId}
              optionValue={"propertyId"}
              optionLabel="buildingName"
              name="propertyId"
              key={`propertyId-${propertyId}`}
            />
            <Calendar
              value={startAppointmentDate}
              onChange={handleFormFieldChange}
              readOnlyInput
              placeholder={t("appointmentDateFrom")}
              name="startAppointmentDate"
              dateFormat="dd/mm/yy"
              showIcon
              ref={startDateRef}
              onHide={() => onHideCalendar(startDateRef)}
              onShow={() => onShowCalendar(startDateRef)}
              key={`startAppointmentDate-${startAppointmentDate?.toString()}`}
            />

            <Calendar
              value={endAppointmentDate}
              onChange={handleFormFieldChange}
              readOnlyInput
              placeholder={t("appointmentDateTo")}
              name="endAppointmentDate"
              dateFormat="dd/mm/yy"
              minDate={
                !startAppointmentDate
                  ? new Date(-8640000000000000)
                  : convertIsoStringToDate(startAppointmentDate)
              }
              ref={endDateRef}
              onHide={() => onHideCalendar(endDateRef)}
              onShow={() => onShowCalendar(endDateRef)}
              key={`endAppointmentDate-${endAppointmentDate?.toString()}`}
            />
            {isCivilDefenseUser() && (
              <Dropdown
                filter
                resetFilterOnHide
                emptyFilterMessage={t("noResultsFound")}
                options={citiesOptions}
                placeholder={t("City")}
                className="city-dropdown"
                optionLabel={getFieldName(language)}
                optionValue="cityId"
                panelClassName="status-dropdown-panel"
                onChange={handleCityChange}
                value={cityId}
                name="cityId"
              />
            )}
            {isCivilDefenseUser() ? (
              <MultiSelect
                filter
                resetFilterOnHide
                emptyMessage={t("noResultsFound")}
                emptyFilterMessage={t("noResultsFound")}
                options={cityId ? serviceTerritories : []}
                placeholder={t("territory") + " - " + t("selectCityFirstShort")}
                className="status-dropdown"
                optionValue={"territoryId"}
                optionLabel={getFieldName(language)}
                panelClassName="status-dropdown-panel"
                onChange={handleFormFieldChange}
                value={territoryId}
                name="territoryId"
              />
            ) : (
              <Dropdown
                filter
                resetFilterOnHide
                emptyFilterMessage={t("noResultsFound")}
                options={allServiceTerritories}
                placeholder={t("territory")}
                className="service-territory-dropdown"
                optionValue="territoryId"
                optionLabel={getFieldName(language)}
                panelClassName="status-dropdown-panel"
                onChange={handleFormFieldChange}
                value={territoryId}
                name="territoryId"
              />
            )}
            {isCivilDefenseUser() && (
              <Dropdown
                filter
                id="company"
                name="company"
                value={company}
                optionValue={"id"}
                optionLabel={"name"}
                onChange={handleFormFieldChange}
                options={bmcList}
                placeholder={t("company")}
                panelClassName="status-dropdown-panel"
              />
            )}

            {!isBreached && (
              <Dropdown
                filter
                resetFilterOnHide
                emptyFilterMessage={t("noResultsFound")}
                options={sLAOptions}
                placeholder={t("sLABreached")}
                className="sla-dropdown"
                optionLabel="id"
                panelClassName="status-dropdown-panel"
                onChange={handleFormFieldChange}
                optionValue={"value"}
                value={slaBreached}
                name="slaBreached"
              />
            )}
            {!isCivilDefenseUser() && (
              <input
                placeholder={t("contractNumber")}
                onChange={handleFormFieldChange}
                value={contractNumber}
                name="contractNumber"
                className="contract_number-field"
              />
            )}

             <InputText
              type="text"
              placeholder={t("PRPNumber")}
              onChange={handleFormFieldChange}
              value={PRPNumber}
              name="PRPNumber"
              autoComplete="off"
            />
            <InputText
                type="text"
                placeholder={t("propertyTradeLicense")}
                onChange={handleFormFieldChange}
                value={propertyTradeLicense}
                name="propertyTradeLicense"
                className="status-dropdown"
              />

              {isCivilDefenseUser() && (
              <InputText
                type="text"
                placeholder={t("companyTradeLicenseNumber")}
                onChange={handleFormFieldChange}
                value={companyTradeLicenseNumber}
                name="companyTradeLicenseNumber"
                className="status-dropdown"
              />
            )}

            <FilterActionButtons
              handleFilter={handleFilter}
              handleClear={handleClearFilterForm}
            />
          </div>
        </form>
      </div>
    </>
  );
};

export default PreventiveAppointmentFilter;
